import { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import {
  Container as MuiContainer,
  Paper,
  Typography,
  Box,
} from '@material-ui/core';
import { Search, Save, X, Users, Shield, Edit2, Loader, BlocksIcon, CheckSquare, XSquare } from 'lucide-react';
import {
  UNIQUE_TILES,
  PERMISSION_TYPES,
  USER_TYPE_LABELS,
  getTileById
} from '../../constants/tilesConfig';
import { useAuth } from '../../context/GlobalContext';

const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 2rem;
`;

const Content = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: white;
  gap: 1rem;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  text-align: center;
  color: #e53e3e;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const WarningBanner = styled.div`
  background: #fff3cd;
  border: 2px solid #ffc107;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #856404;
  font-weight: 500;
`;

const EmptyState = styled.div`
  background: white;
  padding: 3rem 2rem;
  border-radius: 16px;
  text-align: center;
  color: #718096;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

  h3 {
    color: #2d3748;
    margin: 1rem 0 0.5rem 0;
  }
`;

const Header = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Subtitle = styled.p`
  color: #718096;
  margin: 0;
  font-size: 1rem;
`;

const SearchBar = styled.div`
  position: relative;
  margin-top: 1.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.875rem 1rem 0.875rem 3rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 1.5rem;
`;

const UserCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
`;

const UserHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f7fafc;
`;

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${props => props.color || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.h3`
  margin: 0 0 0.25rem 0;
  font-size: 1.125rem;
  color: #2d3748;
  font-weight: 600;
`;

const UserMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  width: fit-content;
  background: ${props => {
    if (props.type === 'admin') return '#ebf4ff';
    if (props.type === 'supervisor') return '#f0fff4';
    return '#fef5e7';
  }};
  color: ${props => {
    if (props.type === 'admin') return '#3182ce';
    if (props.type === 'supervisor') return '#38a169';
    return '#d69e2e';
  }};
`;

const Designation = styled.span`
  font-size: 0.875rem;
  color: #718096;
`;

const PermissionsSection = styled.div`
  margin-top: 1rem;
`;

const SectionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PermissionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const PermissionChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: ${props => props.active ? '#48bb78' : '#e2e8f0'};
  color: ${props => props.active ? 'white' : '#4a5568'};
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: ${props => props.active ? 'default' : 'pointer'};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#38a169' : '#cbd5e0'};
    transform: ${props => props.active ? 'none' : 'scale(1.05)'};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 2px solid #f7fafc;
`;

const Button = styled.button`
  flex: 1;
  padding: 0.625rem 1rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s;

  ${props => props.primary && `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  `}

  ${props => props.secondary && `
    background: #f7fafc;
    color: #4a5568;

    &:hover:not(:disabled) {
      background: #edf2f7;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2d3748;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  color: #718096;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s;

  &:hover {
    background: #f7fafc;
    color: #2d3748;
  }
`;

const PermissionGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const PermissionItem = styled.div`
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.2s;

  ${props => props.selected && `
    border-color: #667eea;
    background: #f7faff;
  `}
`;

const PermissionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const PermissionName = styled.div`
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const SubPermissions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const SubPermissionChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: ${props => props.checked ? '#667eea' : '#f7fafc'};
  color: ${props => props.checked ? 'white' : '#4a5568'};
  border-radius: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.checked ? '#5568d3' : '#edf2f7'};
  }

  input {
    display: none;
  }
`;

const CategoryLabel = styled.div`
  font-size: 0.75rem;
  color: #718096;
  background: #f7fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  margin-left: 0.5rem;
`;

const ADMIN_USER_ID = '66349cf12d0f294182dea252'

const PermissionManagement = () => {
  const { state } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [tempPermissions, setTempPermissions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Check if current user is admin
  const isAdmin = state?.user?._id === ADMIN_USER_ID;

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  // Show access denied if not admin
  if (!isAdmin) {
    return (
      <Container>
        <Content>
          <MuiContainer maxWidth="sm" style={{ marginTop: '5rem' }}>
            <Paper elevation={3} style={{ padding: '4rem 2rem', textAlign: 'center' }}>
              <Box mb={3}>
                <BlocksIcon style={{ fontSize: '4rem', color: '#e53e3e' }} />
              </Box>
              <Typography variant="h4" gutterBottom style={{ fontWeight: 600, color: '#2d3748' }}>
                Access Denied
              </Typography>
              <Typography variant="body1" color="textSecondary" style={{ lineHeight: 1.6 }}>
                You don't have rights to access user permission management.
              </Typography>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  This page is restricted to authorized administrators only.
                </Typography>
              </Box>
            </Paper>
          </MuiContainer>
        </Content>
      </Container>
    );
  }

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get("api/get/user");
      const normalizedUsers = response.data.map(user => ({
        ...user,
        access: Array.isArray(user.access) ? user.access : []
      }));
      setUsers(normalizedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const userName = user.name || '';
    const username = user.username || '';
    return userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      username.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const getUserTypeLabel = (userType) => {
    return USER_TYPE_LABELS[userType] || { label: 'User', type: 'user' };
  };

  const handleEditPermissions = (user) => {
    setEditingUser(user);
    const userAccess = Array.isArray(user.access) ? user.access : [];
    setTempPermissions(JSON.parse(JSON.stringify(userAccess)));
  };

  const toggleModuleAccess = (moduleId) => {
    const exists = tempPermissions.find(p => p.id === moduleId);
    if (exists) {
      setTempPermissions(tempPermissions.filter(p => p.id !== moduleId));
    } else {
      setTempPermissions([...tempPermissions, { id: moduleId, permissions: ['view'] }]);
    }
  };

  const toggleSubPermission = (moduleId, permission) => {
    setTempPermissions(tempPermissions.map(p => {
      if (p.id === moduleId) {
        const hasPermission = p.permissions.includes(permission);
        if (hasPermission) {
          return { ...p, permissions: p.permissions.filter(perm => perm !== permission) };
        } else {
          return { ...p, permissions: [...p.permissions, permission] };
        }
      }
      return p;
    }));
  };

  // Add all permissions
  const addAllPermissions = () => {
    const allPermissions = UNIQUE_TILES.map(tile => ({
      id: tile.id,
      permissions: ['view', 'edit', 'delete', 'create']
    }));
    setTempPermissions(allPermissions);
  };

  console.log(UNIQUE_TILES)

  // Reset all permissions
  const resetAllPermissions = () => {
    setTempPermissions([]);
  };

  const savePermissions = async () => {
    try {
      setSaving(true);
      await axios.put(`api/update/user/${editingUser._id}/permissions`, {
        access: tempPermissions
      });

      setUsers(users.map(u =>
        u._id === editingUser._id
          ? { ...u, access: tempPermissions }
          : u
      ));
      setEditingUser(null);
      alert('Permissions updated successfully!');
    } catch (err) {
      console.error('Error saving permissions:', err);
      alert('Failed to save permissions. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getAvatarColor = (name) => {
    const colors = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Container>
      <Content>
        <Header>
          <Title>
            <Shield size={32} />
            Permission Management
          </Title>
          <Subtitle>Manage user access and permissions across all modules</Subtitle>
          <SearchBar>
            <SearchIcon size={20} />
            <SearchInput
              type="text"
              placeholder="Search users by name or username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
        </Header>

        {loading ? (
          <LoadingContainer>
            <Loader size={48} />
            <p>Loading users...</p>
          </LoadingContainer>
        ) : error ? (
          <ErrorContainer>
            <h3>Error</h3>
            <p>{error}</p>
            <Button primary onClick={fetchUsers} style={{ marginTop: '1rem', width: 'auto', display: 'inline-flex' }}>
              Try Again
            </Button>
          </ErrorContainer>
        ) : filteredUsers.length === 0 ? (
          <EmptyState>
            <Users size={48} style={{ margin: '0 auto' }} />
            <h3>No Users Found</h3>
            <p>
              {searchTerm
                ? `No users match "${searchTerm}"`
                : 'No users available in the system'
              }
            </p>
          </EmptyState>
        ) : (
          <Grid>
            {filteredUsers.map(user => {
              const userTypeInfo = getUserTypeLabel(user.userType);
              return (
                <UserCard key={user._id}>
                  <UserHeader>
                    <Avatar color={getAvatarColor(user.name || 'User')}>
                      {(user.name || 'U').charAt(0).toUpperCase()}
                    </Avatar>
                    <UserInfo>
                      <UserName>{user.name || 'Unknown User'}</UserName>
                      <UserMeta>
                        <Badge type={userTypeInfo.type}>{userTypeInfo.label}</Badge>
                        <Designation>{user.designation || 'No designation'}</Designation>
                      </UserMeta>
                    </UserInfo>
                  </UserHeader>

                  <PermissionsSection>
                    <SectionTitle>
                      <Users size={16} />
                      Active Permissions ({Array.isArray(user.access) ? user.access.length : 0})
                    </SectionTitle>
                    <PermissionsList>
                      {Array.isArray(user.access) && user.access.slice(0, 6).map(access => {
                        const tile = getTileById(access.id);
                        return (
                          <PermissionChip key={access.id} active>
                            {tile?.icon} {tile?.name || `Module ${access.id}`}
                          </PermissionChip>
                        );
                      })}
                      {Array.isArray(user.access) && user.access.length > 6 && (
                        <PermissionChip>+{user.access.length - 6} more</PermissionChip>
                      )}
                      {(!user.access || user.access.length === 0) && (
                        <PermissionChip>No permissions assigned</PermissionChip>
                      )}
                    </PermissionsList>
                  </PermissionsSection>

                  <ButtonGroup>
                    <Button primary onClick={() => handleEditPermissions(user)}>
                      <Edit2 size={16} />
                      Edit Permissions
                    </Button>
                  </ButtonGroup>
                </UserCard>
              );
            })}
          </Grid>
        )}

        {editingUser && (
          <Modal onClick={() => setEditingUser(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Edit Permissions - {editingUser.name}</ModalTitle>
                <CloseButton onClick={() => setEditingUser(null)}>
                  <X size={24} />
                </CloseButton>
              </ModalHeader>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <Button primary onClick={addAllPermissions}>
                  <CheckSquare size={16} />
                  Add All Permissions
                </Button>
                <Button secondary onClick={resetAllPermissions}>
                  <XSquare size={16} />
                  Reset All Permissions
                </Button>
              </div>

              <PermissionGrid>
                {UNIQUE_TILES.map(tile => {
                  const hasAccess = tempPermissions.find(p => p.id === tile.id);
                  return (
                    <PermissionItem key={tile.id} selected={!!hasAccess}>
                      <PermissionHeader>
                        <PermissionName>
                          <span>{tile.icon}</span>
                          <span>{tile.name}</span>
                          <CategoryLabel>
                            {tile.sharedWithSupervisor
                              ? "Admin and Supervisor"
                              : tile.userType === 2
                                ? "Admin"
                                : "Supervisor"}
                          </CategoryLabel>
                        </PermissionName>
                        <Checkbox
                          type="checkbox"
                          checked={!!hasAccess}
                          onChange={() => toggleModuleAccess(tile.id)}
                        />
                      </PermissionHeader>
                      {hasAccess && (
                        <SubPermissions>
                          {PERMISSION_TYPES.map(perm => (
                            <SubPermissionChip
                              key={perm}
                              checked={hasAccess.permissions.includes(perm)}
                            >
                              <input
                                type="checkbox"
                                checked={hasAccess.permissions.includes(perm)}
                                onChange={() => toggleSubPermission(tile.id, perm)}
                              />
                              {perm}
                            </SubPermissionChip>
                          ))}
                        </SubPermissions>
                      )}
                    </PermissionItem>
                  );
                })}
              </PermissionGrid>

              <ButtonGroup style={{ marginTop: '2rem' }}>
                <Button secondary onClick={() => setEditingUser(null)} disabled={saving}>
                  <X size={16} />
                  Cancel
                </Button>
                <Button primary onClick={savePermissions} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Changes
                    </>
                  )}
                </Button>
              </ButtonGroup>
            </ModalContent>
          </Modal>
        )}
      </Content>
    </Container>
  );
};

export default PermissionManagement;
